/**
 * @file index.tsx
 *
 * @fileoverview Show a footer block with the "footer" menu items.
 */

import { useQuery } from '@apollo/client';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import LazyHydrate from 'react-lazy-hydration';
import { Branding } from '~/jobiqo/components/Branding';
import CMPPreferencesButton from '~/jobiqo/components/CMPPreferencesButton';
import { FooterSocialMediaLinks } from '~/jobiqo/components/Footer/SocialMediaLinks';
import FooterPostMenusSlot from '~/jobiqo/components/Slots/FooterPostMenusSlot';
import { AppContext, PathContext } from '~/jobiqo/context/app';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { AppContextInterface, PathContextInterface } from '~/jobiqo/models/app';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { Grid, GridColumnsProp } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { WYSIWYGText } from '~/vcl/atoms/13-text/02-WYSIWYG';
import { Footer as FooterBlock } from '~/vcl/molecules/03-layout/03-footer';
import GET_FOOTER_INDIVIDUAL from './footer-individual.gql';
import GET_FOOTER_MENUS from './footer-menus.gql';
import FooterLink from './Link/FooterLink';
import Text from '~/vcl/atoms/13-text/03-text';

const LanguageSwitcher = dynamic(
  import('~/jobiqo/components/LanguageSwitcher'),
);

export const Footer = () => {
  const pathContext: PathContextInterface = useContext(PathContext);
  const appContext: AppContextInterface = useContext(AppContext);
  const { APP } = useFrontendConfig();
  const router = useRouter();
  const SHOW_POWERED_BY_FOOTER = APP.SHOW_POWERED_BY_FOOTER;
  const SHOW_SECONDARY_FOOTER_LOGO = APP.SHOW_SECONDARY_FOOTER_LOGO;
  const POWERED_BY_LINK = APP.POWERED_BY_LINK;
  const POWERED_BY_TEXT = APP.POWERED_BY_TEXT;
  const POWERED_BY_ATTRIBUTES = APP.POWERED_BY_ATTRIBUTES;
  const CUSTOM_SECONDARY_FOOTER_LOGO = APP.CUSTOM_SECONDARY_FOOTER_LOGO;
  const SHOW_SOCIAL_LINKS = APP.FOOTER_SHOW_SOCIAL_LINKS;

  const { data: menus, error, loading } = useQuery(GET_FOOTER_MENUS, {
    variables: {
      language: appContext.locale,
    },
  });

  const { data: individual } = useQuery(GET_FOOTER_INDIVIDUAL);

  if (error || loading || !menus) {
    return null;
  }

  let columns = Object.values(menus).length;

  individual && individual.content?.value && columns++;

  const poweredByText = POWERED_BY_TEXT ? (
    <Text fontSize={2} textAlign={'center'}>
      {POWERED_BY_TEXT}
    </Text>
  ) : (
    <FormattedMessage
      id="powered_by_jobiqo"
      defaultMessage={'Powered by Jobiqo'}
    />
  );

  return (
    <LazyHydrate whenVisible={true}>
      <FooterBlock
        logo={
          <FlexItems gap={6} alignItems="center">
            {SHOW_SECONDARY_FOOTER_LOGO && (
              <Branding customLogo={CUSTOM_SECONDARY_FOOTER_LOGO} />
            )}
            {router.locales && router.locales.length > 1 && (
              <LanguageSwitcher />
            )}
          </FlexItems>
        }
        socialLinks={SHOW_SOCIAL_LINKS && <FooterSocialMediaLinks />}
        poweredBy={
          SHOW_POWERED_BY_FOOTER ? (
            <>
              <div>
                {POWERED_BY_LINK ? (
                  <a href={POWERED_BY_LINK} {...POWERED_BY_ATTRIBUTES}>
                    {poweredByText}
                  </a>
                ) : (
                  <div style={{ color: 'white' }}>{poweredByText}</div>
                )}
              </div>
            </>
          ) : null
        }
      >
        <Grid
          justifyItems={['baseline', 'center']}
          alignItems={['start']}
          columns={[1, columns as GridColumnsProp]}
        >
          {individual?.content?.value ? (
            <Cell>
              <div style={{ color: 'white' }}>
                <WYSIWYGText
                  content={individual.content.value}
                  format="full_html"
                />
              </div>
            </Cell>
          ) : null}
          {Object.values(menus).map((menu: any, menuIndex, { length }) => {
            return (
              menu?.items?.length > 0 && (
                <Cell key={menuIndex}>
                  <div className="vcl-footer-section-title">{menu.name}</div>
                  <ul>
                    {menu.items.map((footerItem, index) => (
                      <li key={index}>
                        <FooterLink
                          link={footerItem}
                          pathname={pathContext.pathname}
                        />
                      </li>
                    ))}
                    {length - 1 === menuIndex && <CMPPreferencesButton />}
                  </ul>
                </Cell>
              )
            );
          })}
        </Grid>
        <FooterPostMenusSlot />
      </FooterBlock>
    </LazyHydrate>
  );
};
export default Footer;
